import { sha256HexSync } from "./sha256Sync";

export const STRUCTURED_INGREDIENT_UNITS = Object.freeze([
  "g",
  "kg",
  "ml",
  "l",
  "piece",
  "tbsp",
  "tsp",
  "portion",
  "pinch",
  "to_taste",
]);

export const STRUCTURED_INGREDIENT_CATEGORIES = Object.freeze([
  "protein",
  "vegetable",
  "fruit",
  "starch",
  "fresh",
  "fat",
  "grocery",
  "herb_spice",
  "other",
]);

const UNIT_SET = new Set(STRUCTURED_INGREDIENT_UNITS);
const CATEGORY_SET = new Set(STRUCTURED_INGREDIENT_CATEGORIES);
const FOOD_KEY_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function cleanText(value, maxLength) {
  if (typeof value !== "string") return null;
  const cleaned = value.replace(/\s+/g, " ").trim();
  return cleaned && cleaned.length <= maxLength ? cleaned : null;
}

export function canonicalFoodKey(value) {
  const cleaned = cleanText(value, 120);
  if (!cleaned) return null;
  const key = cleaned
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr")
    .replace(/[’']/g, "-")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return key && FOOD_KEY_RE.test(key) ? key : null;
}

function normalizeUnit(value) {
  if (typeof value !== "string") return null;
  const aliases = {
    "c.s.": "tbsp",
    "c.s": "tbsp",
    cs: "tbsp",
    cas: "tbsp",
    "c à soupe": "tbsp",
    "c. à soupe": "tbsp",
    "cuillère à soupe": "tbsp",
    "cuillères à soupe": "tbsp",
    "cuillere a soupe": "tbsp",
    "cuilleres a soupe": "tbsp",
    tablespoon: "tbsp",
    tablespoons: "tbsp",
    "c.c.": "tsp",
    "c.c": "tsp",
    cc: "tsp",
    cac: "tsp",
    "c à café": "tsp",
    "c. à café": "tsp",
    "cuillère à café": "tsp",
    "cuillères à café": "tsp",
    "cuillere a cafe": "tsp",
    "cuilleres a cafe": "tsp",
    teaspoon: "tsp",
    teaspoons: "tsp",
    gr: "g",
    gramme: "g",
    grammes: "g",
    kilogramme: "kg",
    kilogrammes: "kg",
    millilitre: "ml",
    millilitres: "ml",
    litre: "l",
    litres: "l",
    unite: "piece",
    unité: "piece",
    unites: "piece",
    unités: "piece",
    piece: "piece",
    pièce: "piece",
    pieces: "piece",
    pièces: "piece",
    portion: "portion",
    portions: "portion",
    pincée: "pinch",
    pincées: "pinch",
    pincee: "pinch",
    pincees: "pinch",
    "selon le goût": "to_taste",
    "selon le gout": "to_taste",
  };
  const normalized = value.replace(/\s+/g, " ").trim().toLocaleLowerCase("fr");
  const direct = aliases[normalized] || (UNIT_SET.has(normalized) ? normalized : null);
  if (direct) return direct;
  // Les abréviations de cuillère s'écrivent de dix façons : « c.à.c. », « càc »,
  // « c. à c. », « c.a.s »… Plutôt que d'allonger la table indéfiniment, on
  // réduit la forme abrégée à ses lettres et on tranche sur la dernière.
  const lettres = normalized
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z]/g, "");
  if (lettres === "cc" || lettres === "cac") return "tsp";
  if (lettres === "cs" || lettres === "cas") return "tbsp";
  return null;
}

function normalizeQuantity(value, unit) {
  if (unit === "to_taste") return value === null ? null : undefined;
  let candidate = value;
  if (typeof value === "string") {
    const cleaned = value.trim();
    // Conversion lossless uniquement : pas de fraction, d'unité, d'exposant
    // ni d'intervalle interprété silencieusement.
    if (!/^\d+(?:[.,]\d+)?$/.test(cleaned)) return undefined;
    candidate = Number(cleaned.replace(",", "."));
  }
  return typeof candidate === "number"
    && Number.isFinite(candidate)
    && candidate > 0
    && candidate <= 100_000
    ? candidate
    : undefined;
}

function normalizeCategory(value) {
  if (typeof value !== "string") return "other";
  const normalized = value.trim().toLocaleLowerCase("fr");
  const aliases = {
    grain: "starch",
    cereal: "starch",
    dairy: "fresh",
    spice: "herb_spice",
  };
  return aliases[normalized] || (CATEGORY_SET.has(normalized) ? normalized : "other");
}

function deterministicIngredientId({ foodKey, label, unit, preparation }, index) {
  const fingerprint = JSON.stringify({ foodKey, label, unit, preparation, index });
  return `ingredient-${sha256HexSync(fingerprint).slice(0, 20)}`;
}

function structuredIssue(code, index, field) {
  return Object.freeze({ code, index, field });
}

/**
 * Normalise seulement les écarts de format sans modifier le contenu culinaire.
 * Le diagnostic ne contient ni valeur, ni libellé, ni autre donnée cliente.
 */
export function normalizeStructuredIngredientsWithDiagnostics(value) {
  if (!Array.isArray(value) || value.length === 0 || value.length > 25) {
    return {
      ingredients: null,
      issue: structuredIssue("INVALID_INGREDIENTS_ARRAY", null, "ingredients_structured"),
    };
  }
  const normalized = [];
  for (let index = 0; index < value.length; index += 1) {
    const ingredient = value[index];
    if (!ingredient || typeof ingredient !== "object" || Array.isArray(ingredient)) {
      return { ingredients: null, issue: structuredIssue("INVALID_INGREDIENT", index, "ingredient") };
    }
    const label = cleanText(ingredient.label, 120);
    if (!label) {
      return { ingredients: null, issue: structuredIssue("INVALID_INGREDIENT_FIELD", index, "label") };
    }
    // foodKey est un identifiant technique : le recalcul depuis le libellé
    // intact ne change aucun aliment et évite de rejeter un slug mal formé.
    const foodKey = canonicalFoodKey(ingredient.foodKey) || canonicalFoodKey(label);
    const unit = normalizeUnit(ingredient.unit);
    const preparation = ingredient.preparation === ""
      || ingredient.preparation === null
      || ingredient.preparation === undefined
      ? ""
      : cleanText(ingredient.preparation, 120);
    const category = normalizeCategory(ingredient.category);
    const quantity = normalizeQuantity(ingredient.quantity, unit);
    if (!foodKey) {
      return { ingredients: null, issue: structuredIssue("INVALID_INGREDIENT_FIELD", index, "foodKey") };
    }
    if (!unit) {
      return { ingredients: null, issue: structuredIssue("INVALID_INGREDIENT_FIELD", index, "unit") };
    }
    if (preparation === null) {
      return { ingredients: null, issue: structuredIssue("INVALID_INGREDIENT_FIELD", index, "preparation") };
    }
    if (quantity === undefined) {
      return { ingredients: null, issue: structuredIssue("INVALID_INGREDIENT_FIELD", index, "quantity") };
    }
    normalized.push({
      id: deterministicIngredientId({ foodKey, label, unit, preparation }, index),
      foodKey,
      label,
      quantity,
      unit,
      preparation,
      category,
    });
  }
  return { ingredients: normalized, issue: null };
}

export function normalizeStructuredIngredients(value) {
  return normalizeStructuredIngredientsWithDiagnostics(value).ingredients;
}

const DISPLAY_UNITS = {
  g: "g",
  kg: "kg",
  ml: "ml",
  l: "l",
  piece: "pièce",
  tbsp: "c.s.",
  tsp: "c.c.",
  portion: "portion",
  pinch: "pincée",
};

export function formatStructuredIngredient(ingredient) {
  const normalized = normalizeStructuredIngredients([ingredient]);
  if (!normalized) return null;
  const item = normalized[0];
  if (item.unit === "to_taste") {
    return `${item.label}${item.preparation ? `, ${item.preparation}` : ""} · selon le goût`;
  }
  const quantity = Number.isInteger(item.quantity)
    ? String(item.quantity)
    : String(item.quantity).replace(".", ",");
  const unit = DISPLAY_UNITS[item.unit] || item.unit;
  return `${quantity} ${unit} de ${item.label}${item.preparation ? `, ${item.preparation}` : ""}`;
}

/** Milieu d'une fourchette, arrondi : à l'unité dès 10, sinon au demi. */
function rangeMidpoint(low, high) {
  if (!(low > 0) || !(high > 0) || high < low) return null;
  const middle = (low + high) / 2;
  return middle >= 10 ? Math.round(middle) : Math.round(middle * 2) / 2;
}

function parseNumber(raw) {
  if (!raw) return null;
  const range = raw.match(/^(\d+(?:[.,]\d+)?)\s*(?:à|a|-|–|—)\s*(\d+(?:[.,]\d+)?)$/);
  if (range) return rangeMidpoint(Number(range[1].replace(",", ".")), Number(range[2].replace(",", ".")));
  const fraction = raw.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (fraction) {
    const denominator = Number(fraction[2]);
    return denominator > 0 ? Number(fraction[1]) / denominator : null;
  }
  const parsed = Number(raw.replace(",", "."));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

// Cuillères : « c.s. », « c. à s. », « c.à.s », « cuillère(s) à soupe » (et
// café). Les unités restent tbsp / tsp : aucune conversion n'est appliquée.
// Repère documenté si une conversion devient nécessaire : SPOON_ML.
const LEGACY_UNIT_RE = "kg|g|ml|l|cuill[eè]res?\\s+(?:[àa]\\s+)?(?:soupe|caf[eé])|c\\.?\\s*(?:[àa]\\.?\\s*)?s\\.?|c\\.?\\s*(?:[àa]\\.?\\s*)?c\\.?|pi[eè]ces?|unit[eé]s?|portions?|pinc[eé]es?";

/** 1 c.s. = 15 ml, 1 c.c. = 5 ml (repère documenté, non appliqué par le parseur). */
export const SPOON_ML = Object.freeze({ tbsp: 15, tsp: 5 });

// Quantité écrite : fourchette (« 120 à 130 », « 120-130 », « 120–130 »),
// nombre ou fraction. Une fourchette donne son milieu ; rien n'est inventé.
const QUANTITY_RE = "\\d+(?:[.,]\\d+)?\\s*(?:à|a|-|–|—)\\s*\\d+(?:[.,]\\d+)?|\\d+(?:[.,]\\d+)?|\\d+\\s*\\/\\s*\\d+";

function legacyUnit(value) {
  const normalized = value.replace(/\s+/g, "").toLocaleLowerCase("fr");
  if (/^c\.?(?:[àa]\.?)?s\.?$/.test(normalized) || /^cuill[eè]res?[àa]?soupe$/.test(normalized)) return "tbsp";
  if (/^c\.?(?:[àa]\.?)?c\.?$/.test(normalized) || /^cuill[eè]res?[àa]?caf[eé]$/.test(normalized)) return "tsp";
  if (/^pi[eè]ce|^unit[eé]/.test(normalized)) return "piece";
  if (/^portion/.test(normalized)) return "portion";
  if (/^pinc[eé]e/.test(normalized)) return "pinch";
  return normalizeUnit(normalized);
}

export function parseLegacyIngredientLine(value, { requireExplicitLegacyUnit = false } = {}) {
  const line = cleanText(value, 300);
  if (!line) return null;
  const prefix = new RegExp(`^(${QUANTITY_RE})\\s*(${LEGACY_UNIT_RE})?\\s+(?:de\\s+|d['’]\\s*)?(.+)$`, "i");
  const suffix = new RegExp(`^(.+?)\\s+(${QUANTITY_RE})\\s*(${LEGACY_UNIT_RE})$`, "i");
  const prefixMatch = line.match(prefix);
  const suffixMatch = prefixMatch ? null : line.match(suffix);
  const quantity = parseNumber(prefixMatch?.[1] || suffixMatch?.[2]);
  const explicitUnit = prefixMatch?.[2] || suffixMatch?.[3];
  // Le fallback historique « quantité seule = pièce » reste disponible pour
  // les consommateurs existants. Les contrôles de complétude peuvent activer
  // le mode strict afin de ne pas considérer « 100 poulet » comme 100 pièces.
  if (requireExplicitLegacyUnit && !explicitUnit) return null;
  const unit = legacyUnit(explicitUnit || "piece");
  const label = cleanText(prefixMatch?.[3] || suffixMatch?.[1], 120);
  const foodKey = canonicalFoodKey(label);
  if (!quantity || !unit || !label || !foodKey) return null;
  return normalizeStructuredIngredients([{
    id: "legacy",
    foodKey,
    label,
    quantity,
    unit,
    preparation: "",
  }])?.[0] || null;
}

/**
 * Lit une recette nouvelle ou une ancienne recette simple. Le fallback legacy
 * est volontairement tout-ou-rien pour ne jamais produire une liste de courses
 * partielle en ignorant silencieusement un ingrédient ambigu.
 */
export function readStructuredRecipeIngredients(
  recipe,
  { requireExplicitLegacyUnit = false } = {},
) {
  if (!recipe || typeof recipe !== "object" || Array.isArray(recipe)) return null;
  const fromLines = (strict) => {
    if (!Array.isArray(recipe.ingredients) || recipe.ingredients.length === 0) return null;
    const parsed = recipe.ingredients.map((ingredient) => parseLegacyIngredientLine(
      ingredient,
      { requireExplicitLegacyUnit: strict },
    ));
    return parsed.every(Boolean) ? parsed : null;
  };
  if (Object.prototype.hasOwnProperty.call(recipe, "ingredients_structured")) {
    const structured = normalizeStructuredIngredients(recipe.ingredients_structured);
    // Version structurée vide ou illisible : les lignes écrites par Anissa
    // prennent le relais, seulement si CHACUNE porte une quantité et une
    // unité explicites (rien n'est deviné).
    return structured || fromLines(true);
  }
  return fromLines(requireExplicitLegacyUnit);
}

export function buildRecipeContentId(recipe) {
  const ingredients = readStructuredRecipeIngredients(recipe);
  if (!ingredients) return null;
  const preparation = Array.isArray(recipe?.preparation)
    ? recipe.preparation.map((step) => cleanText(step, 300)).filter(Boolean)
    : [];
  return `recipe-${sha256HexSync(JSON.stringify({ ingredients, preparation })).slice(0, 24)}`;
}
